'use client';

import { useEffect, useRef, useState } from 'react';
import { Content } from '@prismicio/client';
import { Button } from '@/components';
import { useCalculatePayment } from '@/components/Calculator/components/RangeCalculator/utils';
import { GradientCheckmark } from '@/assets/icons/Checkmark';
import { LockIcon } from '@/assets/icons/OfferhubIcons/OfferhubIcons';
import styles from './Calculator.module.scss';

const MIN_LOAN = 1000;
const MAX_LOAN = 250000;

interface Props {
  data: Content.IncomeBasedHeroSlice;
}

const Calculator = ({ data }: Props) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [loanValue, setLoanValue] = useState('25,000');

  const rawNumeric = parseInt(loanValue.replace(/,/g, ''), 10) || 0;
  const numericLoanValue = Math.max(MIN_LOAN, Math.min(rawNumeric, MAX_LOAN));

  const calculatedPayment = useCalculatePayment({
    loanValue: numericLoanValue,
    interestValue: Number(data.primary.rate_value?.replace(/,/g, '') || 0),
    timeLineValue: Number(data.primary.term_value?.replace(/,/g, '') || 0),
    fixedValue: 0,
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const rawValue = e.target.value.replace(/\D/g, '');

    if (rawValue === '') {
      setLoanValue('');
      return;
    }

    const numericValue = Math.min(parseInt(rawValue, 10), MAX_LOAN);
    const formattedValue = numericValue.toLocaleString('en-US');
    setLoanValue(formattedValue);
  };

  const handleBlur = () => {
    const raw = loanValue.replace(/,/g, '');
    if (raw === '') {
      setLoanValue(MIN_LOAN.toLocaleString('en-US'));
      return;
    }
    const num = parseInt(raw, 10);
    const clamped = Math.max(MIN_LOAN, Math.min(num, MAX_LOAN));
    setLoanValue(clamped.toLocaleString('en-US'));
  };

  const handleRangeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const numericValue = Math.max(
      MIN_LOAN,
      Math.min(parseInt(e.target.value, 10), MAX_LOAN)
    );
    setLoanValue(numericValue.toLocaleString('en-US'));
  };

  useEffect(() => {
    if (inputRef.current) {
      const value = Math.max(
        MIN_LOAN,
        Math.min(parseInt(loanValue.replace(/,/g, ''), 10) || 0, MAX_LOAN)
      );
      const percentage = (value / MAX_LOAN) * 100;
      inputRef.current.style.setProperty('--value', `${percentage}%`);
    }
  }, [loanValue]);

  return (
    <article className={styles.wrapper}>
      <h3 className={styles.title}>{data.primary.calculator_title}</h3>
      <div className={styles.controlsWrapper}>
        <p className={styles.label}>{data.primary.input_label}</p>
        <div className={styles.inputWrapper}>
          <span className={styles.prefix}>$</span>
          <input
            className={styles.input}
            type="text"
            inputMode="numeric"
            value={loanValue}
            onChange={handleChange}
            onBlur={handleBlur}
          />
          <input
            type="range"
            min={MIN_LOAN}
            max={MAX_LOAN}
            step={1000}
            value={Math.max(
              MIN_LOAN,
              Math.min(parseInt(loanValue.replace(/,/g, ''), 10) || 0, MAX_LOAN)
            )}
            onChange={handleRangeChange}
            className={styles.rangeInput}
            ref={inputRef}
          />
        </div>
        <span className={styles.rangeNote}>
          Enter amount between $1k and $250k
        </span>
        <div className={styles.quickSelectWrapper}>
          {data.primary.quick_select_values.map((value) => (
            <button
              key={value.value}
              className={styles.quickSelectButton}
              onClick={() =>
                setLoanValue(
                  Math.max(
                    MIN_LOAN,
                    Math.min(value?.value ?? 0, MAX_LOAN)
                  ).toLocaleString('en-US')
                )
              }
            >
              ${Math.round((value?.value ?? 0) / 1000).toLocaleString('en-US')}k
            </button>
          ))}
        </div>
      </div>
      <div className={styles.resultWrapper}>
        <div className={styles.calculationWrapper}>
          <p className={styles.resultLabel}>
            {data.primary.calculator_result_title}
          </p>
          <div className={styles.resultValue}>
            $
            {calculatedPayment.toLocaleString('en-US', {
              minimumFractionDigits: 0,
              maximumFractionDigits: 0,
            })}
            <span>/ month</span>
          </div>
          <div id="blinking-button">
            <Button
              text={data.primary.calculator_button_text}
              link={`${data.primary.calculator_button_link}`}
              styles={`${styles.button} ${styles.desktopButton}`}
              isArrow
              isBlinking
              onClick={() => {
                sessionStorage.setItem('preFilledLoanValue', loanValue);
              }}
            />
            <Button
              link={`${data.primary.calculator_button_link}`}
              text={data.primary.calculator_mobile_button_text}
              styles={`${styles.button} ${styles.mobileButton}`}
              isArrow
              isBlinking
              onClick={() => {
                sessionStorage.setItem('preFilledLoanValue', loanValue);
              }}
            />
          </div>
          <div className={`${styles.applyLabel} ${styles.desktopLabel}`}>
            <GradientCheckmark />
            <span>{data.primary.calculator_label}</span>
          </div>
          <div className={`${styles.applyLabel} ${styles.mobileLabel}`}>
            <LockIcon />
            <span>{data.primary.calculator_mobile_label}</span>
          </div>
        </div>
        <div className={styles.labelWrapper}>
          <p className={styles.labelTitle}>{data.primary.term_title}</p>
          <p className={styles.labelValue}>{data.primary.term_value} months</p>
        </div>
        <div className={styles.labelWrapper}>
          <p className={styles.labelTitle}>{data.primary.rate_title}</p>
          <p className={styles.labelValue}>{data.primary.rate_value}% APR</p>
        </div>
      </div>
      <span className={styles.note}>{data.primary.calculator_note}</span>
    </article>
  );
};

export default Calculator;
